import type { ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { IdentityVerificationResult } from '#src/components/fiatWallet/CreateFiatWallet/HostedIdentityVerification/IdentityVerificationResult/index.tsx';
import { HostedIdentityVerification } from '#src/components/fiatWallet/CreateFiatWallet/HostedIdentityVerification/index.tsx';
import type { JONUM_FLOW } from '#src/components/fiatWallet/CreateFiatWallet/HostedIdentityVerification/returnUrl.ts';
import { HostedIdentityVerificationRefused } from '#src/components/fiatWallet/CreateFiatWallet/HostedIdentityVerificationRefused/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';

import { ReviewInfo } from './ReviewInfo.tsx';

enum KycStep {
  REVIEW_INFO = 'REVIEW_INFO',
  HOSTED_IDENTITY_VERIFICATION = 'HOSTED_IDENTITY_VERIFICATION',
  VERIFICATION_RESULT = 'VERIFICATION_RESULT',
  VERIFICATION_REFUSED = 'VERIFICATION_REFUSED',
}

const Body = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
  min-height: 100%;
  justify-content: flex-start;
`;

type Props = {
  onDone: () => void;
  renderVerificationInProgress?: (props: { onDone: () => void }) => ReactNode;
  hostedVerificationFlow?: typeof JONUM_FLOW;
};

export const KycFlow = ({
  onDone,
  renderVerificationInProgress,
  hostedVerificationFlow,
}: Props) => {
  const { fiatWalletAccountable } = useCurrentUserContext();
  const hadWalletAtMount = useState(() => !!fiatWalletAccountable)[0];
  const [step, setStep] = useState<KycStep>(
    hadWalletAtMount
      ? KycStep.HOSTED_IDENTITY_VERIFICATION
      : KycStep.REVIEW_INFO
  );

  const onBack = () => {
    if (hadWalletAtMount) {
      onDone();
    } else {
      setStep(KycStep.REVIEW_INFO);
    }
  };

  if (step === KycStep.REVIEW_INFO) {
    return (
      <Body>
        <ReviewInfo
          onNext={() => setStep(KycStep.HOSTED_IDENTITY_VERIFICATION)}
        />
      </Body>
    );
  }

  if (step === KycStep.HOSTED_IDENTITY_VERIFICATION) {
    return (
      <Body>
        <HostedIdentityVerification
          onBack={onBack}
          flow={hostedVerificationFlow}
          onVerificationSubmitted={() => setStep(KycStep.VERIFICATION_RESULT)}
        />
      </Body>
    );
  }

  if (step === KycStep.VERIFICATION_RESULT) {
    return (
      <Body>
        <IdentityVerificationResult
          onDone={onDone}
          renderVerificationInProgress={renderVerificationInProgress}
          onRefused={() => setStep(KycStep.VERIFICATION_REFUSED)}
        />
      </Body>
    );
  }

  return (
    <Body>
      <HostedIdentityVerificationRefused
        flow={hostedVerificationFlow}
        onReviewInfo={() => setStep(KycStep.REVIEW_INFO)}
        onContactSupport={onDone}
        onVerificationSubmitted={() => setStep(KycStep.VERIFICATION_RESULT)}
      />
    </Body>
  );
};
